import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import MainTitle from "../common/MainTitle";
import AccessDeniedResponse from "../common/AccessDeniedResponse";
import RowPhoto from "../common/TdImg";
import {
  EmptyState,
  LoadingState,
  SectionCard,
  StatusBadge,
} from "../common/SurfaceUI";

import lbl from "../../data/Labels.json";
import db from "../../services/query";
import "./applicantDetail.css";
import ApplicantActions, { ApplicantStepEditor } from './ApplicantActions';
import useParityAccess from '../common/useParityAccess';

const dash = (value) =>
  value != null && value !== "" ? String(value) : "—";

const Field = ({ label, value }) => (
  <div className="applicant-field">
    <dt className="applicant-field-label">{label}</dt>
    <dd className="applicant-field-value">{dash(value)}</dd>
  </div>
);

/**
 * Read-only applicant detail with Testaria step-oriented sections.
 * Writes / step mutations remain BLOCKED (Admissions write sprint).
 */
const ApplicantDetail = () => {
  const { id } = useParams();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [tab, setTab] = useState("personal");
  const [editStep,setEditStep]=useState(null);
  const can=useParityAccess();
  const reload=async()=>{const {data}=await db.getAll(`students/applicants/${id}`);setRow(data.data?.[0] || null);};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDenied(false);
      setErrorMsg("");
      try {
        const { data: body } = await db.getAll(`students/applicants/${id}`);
        if (cancelled) return;
        const item = Array.isArray(body?.data) ? body.data[0] : body?.data;
        setRow(item || null);
        if (!item) setErrorMsg(lbl.RecordNotFound);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const body = err?.response?.data;
        if (status === 403 || body?.error?.statusCode === 403) {
          setDenied(true);
        } else if (status === 404) {
          setErrorMsg(lbl.RecordNotFound);
        } else {
          setErrorMsg(body?.message || "بارگیری جزئیات ناموفق بود.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const fullName = row
    ? [row.student_name, row.student_lastname].filter(Boolean).join(" ")
    : "";
  const fullNameEn = row
    ? [row.student_name_en, row.student_lastname_en].filter(Boolean).join(" ")
    : "";
  const complete = row && Number(row.registration_complete) === 1;

  const tabs = [
    { id: "personal", label: "اطلاعات شخصی" },
    { id: "contact", label: "تماس و سکونت" },
    { id: "education", label: "سوابق تعلیمی" },
    { id: "kankor", label: "کانکور" },
    { id: "field", label: "رشته و ثبت‌نام" },
    { id: "status", label: "وضعیت و اسناد" },
  ];

  return (
    <div className="main-content applicant-detail mis-surface">
      <div className="main-content-header">
        <div className="right-content">
          <MainTitle title={lbl.ApplicantDetail} />
          <p className="applicant-page-subtitle">پروفایل اداری و اطلاعات ثبت‌نام</p>
        </div>
        <div className="center-content" />
        <div className="left-content">
          <Link className="applicant-back-link mis-button is-secondary" to="/mis/applicants">
            ← {lbl.Applicants}
          </Link>
        </div>
      </div>

      <div className="main-content-body">
        {denied ? (
          <AccessDeniedResponse />
        ) : loading ? (
          <LoadingState label={lbl.Loading} />
        ) : errorMsg ? (
          <EmptyState
            tone="error"
            role="alert"
            title={errorMsg === lbl.RecordNotFound ? lbl.RecordNotFound : "بارگیری پروفایل ناموفق بود"}
            description={
              errorMsg === lbl.RecordNotFound
                ? "داوطلب مورد نظر پیدا نشد یا دیگر در دسترس نیست."
                : errorMsg
            }
          />
        ) : row ? (
          <>
            <ApplicantActions row={row} detail onSaved={reload}/>
            {can('edit-applicant','insert-applicant') && <div className="parity-actions">{['ثبت نام','اطلاعات شخصی','مشخصات تعلیمی','انتخاب رشته'].map((name,i)=><button key={name} className="mis-button is-secondary" onClick={()=>setEditStep(i+1)}>ویرایش {name}</button>)}</div>}
            {editStep && <ApplicantStepEditor row={row} step={editStep} onClose={()=>setEditStep(null)} onSaved={reload}/>}
            <section className="applicant-hero">
              <div className="applicant-hero-photo">
                <RowPhoto src={row.student_photo} />
              </div>
              <div className="applicant-hero-meta">
                <span className="applicant-eyebrow">پروفایل داوطلب</span>
                <h2>{fullName || "—"}</h2>
                {fullNameEn ? <p className="applicant-en-name">{fullNameEn}</p> : null}
                <div className="applicant-hero-chips">
                  <StatusBadge tone="info">آریا: {dash(row.aria_id)}</StatusBadge>
                  <StatusBadge tone="neutral">نیکسا: {dash(row.nexa_id)}</StatusBadge>
                  <StatusBadge tone={complete ? "success" : "warning"}>
                    {complete ? lbl.ApplicantsComplete : lbl.ApplicantsIncomplete}
                  </StatusBadge>
                </div>
                <dl className="applicant-hero-facts">
                  <div>
                    <dt>پوهنحی</dt>
                    <dd>{dash(row.faculty_name || row.faculty)}</dd>
                  </div>
                  <div>
                    <dt>دیپارتمنت</dt>
                    <dd>{dash(row.department)}</dd>
                  </div>
                  <div>
                    <dt>سال ورود / سمستر</dt>
                    <dd>{dash(row.entry_year)}</dd>
                  </div>
                  <div>
                    <dt>شناسه داوطلب</dt>
                    <dd>{dash(row.student_id)}</dd>
                  </div>
                </dl>
                <p className="applicant-write-note">
                  ویرایش اطلاعات با انتخاب مرحله؛ عکس و اسناد از سامانه ثبت‌نام
                  انجام می‌شود.
                </p>
              </div>
            </section>

            <nav
              className="applicant-tabs"
              aria-label="بخش‌های پروفایل داوطلب"
              role="tablist"
            >
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  id={`applicant-tab-${t.id}`}
                  aria-selected={tab === t.id}
                  aria-controls={`applicant-panel-${t.id}`}
                  className={tab === t.id ? "active" : ""}
                  data-testid={`applicant-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            {tab === "personal" ? (
              <SectionCard
                className="applicant-section"
                title="اطلاعات شخصی"
                subtitle="مشخصات هویتی داوطلب به زبان‌های دری و انگلیسی"
                id="applicant-panel-personal"
                role="tabpanel"
                aria-labelledby="applicant-tab-personal"
                data-testid="applicant-section-personal"
              >
                <dl className="applicant-grid">
                  <Field label="نام" value={row.student_name} />
                  <Field label="نام خانوادگی" value={row.student_lastname} />
                  <Field label="نام پدر" value={row.student_father_name} />
                  <Field label="نام پدرکلان" value={row.grand_father_name} />
                  <Field label="نام مادر" value={row.mother_name} />
                  <Field label="جنسیت" value={row.gender_name || row.gender} />
                  <Field label="ایمیل" value={row.student_email || row.email} />
                  <Field label="نام (انگلیسی)" value={row.student_name_en} />
                  <Field label="تخلص (انگلیسی)" value={row.student_lastname_en} />
                  <Field label="نام پدر (انگلیسی)" value={row.student_father_name_en} />
                  <Field label="نام پدرکلان (انگلیسی)" value={row.grand_father_name_en} />
                  <Field label="نام مادر (انگلیسی)" value={row.mother_name_en} />
                </dl>
              </SectionCard>
            ) : null}

            {tab === "contact" ? (
              <SectionCard
                className="applicant-section"
                title="اطلاعات تماس و سکونت"
                subtitle="راه‌های تماس، سرپرست و نشانی‌های ثبت‌شده"
                id="applicant-panel-contact"
                role="tabpanel"
                aria-labelledby="applicant-tab-contact"
                data-testid="applicant-section-contact"
              >
                <dl className="applicant-grid">
                  <Field label="شماره تماس" value={row.student_phone} />
                  <Field label="تماس والدین" value={row.parent_phone} />
                  <Field label="وظیفه پدر" value={row.parent_job} />
                  <Field label="حمایت کننده مالی" value={row.supporter_name || row.supporter} />
                  <Field label="ولایت اصلی" value={row.permanent_city} />
                  <Field label="آدرس اصلی" value={row.permanent_address} />
                  <Field label="سکونت فعلی" value={row.current_city} />
                  <Field label="آدرس فعلی" value={row.current_address} />
                  <Field label="تذکره" value={row.tazkera_no} />
                </dl>
              </SectionCard>
            ) : null}

            {tab === "education" ? (
              <SectionCard
                className="applicant-section"
                title="سوابق تعلیمی"
                subtitle="مکتب و سال فراغت داوطلب"
                id="applicant-panel-education"
                role="tabpanel"
                aria-labelledby="applicant-tab-education"
                data-testid="applicant-section-education"
              >
                <dl className="applicant-grid">
                  <Field label="مکتب" value={row.school_name} />
                  <Field label="سال فراغت" value={row.graduation_year} />
                </dl>
              </SectionCard>
            ) : null}

            {tab === "kankor" ? (
              <SectionCard
                className="applicant-section"
                title="اطلاعات کانکور"
                subtitle="شناسه، سال و نمرات کانکور دولتی و آریا"
                id="applicant-panel-kankor"
                role="tabpanel"
                aria-labelledby="applicant-tab-kankor"
                data-testid="applicant-section-kankor"
              >
                <dl className="applicant-grid">
                  <Field label="شماره کانکور دولتی" value={row.university_id || row.kankor_id} />
                  <Field label="سال کانکور دولتی" value={row.university_attendance_year} />
                  <Field label="نمره کانکور دولتی" value={row.university_score} />
                  <Field label="نمره کانکور آریا" value={row.aria_kankor_score} />
                </dl>
              </SectionCard>
            ) : null}

            {tab === "field" ? (
              <SectionCard
                className="applicant-section"
                title="رشته و ثبت‌نام"
                subtitle="انتخاب پوهنحی، دیپارتمنت، زمان و فیس"
                id="applicant-panel-field"
                role="tabpanel"
                aria-labelledby="applicant-tab-field"
                data-testid="applicant-section-field"
              >
                <dl className="applicant-grid">
                  <Field label="سمستر / سال ورود" value={row.entry_year} />
                  <Field label="پوهنحی" value={row.faculty_name || row.faculty} />
                  <Field label="دیپارتمنت" value={row.department} />
                  <Field label="وقت" value={row.time} />
                  <Field label="فیس" value={row.fee} />
                </dl>
              </SectionCard>
            ) : null}

            {tab === "status" ? (
              <SectionCard
                className="applicant-section"
                title="وضعیت ثبت‌نام و اسناد"
                subtitle="نمای کلی تکمیل ثبت‌نام و زمان‌های ثبت سیستم"
                id="applicant-panel-status"
                role="tabpanel"
                aria-labelledby="applicant-tab-status"
                data-testid="applicant-section-status"
              >
                <dl className="applicant-grid">
                  <Field
                    label="وضعیت ثبت‌نام"
                    value={complete ? lbl.ApplicantsComplete : lbl.ApplicantsIncomplete}
                  />
                  <Field label="شناسه داوطلب" value={row.student_id} />
                  <Field label="تاریخ ایجاد" value={row.date_created} />
                  <Field label="آخرین ویرایش" value={row.date_modified} />
                </dl>
                <p className="applicant-write-note">
                  بارگذاری اسناد، تایید یا رد و ویرایش مراحل از سامانه ثبت‌نام انجام
                  می‌شود.
                </p>
              </SectionCard>
            ) : null}
          </>
        ) : (
          <EmptyState title={lbl.RecordNotFound} />
        )}
      </div>
    </div>
  );
};

export default ApplicantDetail;
